import { observer } from 'mobx-react';
import * as React from 'react';
import { WrapRequest } from 'wrap-request';
import { useLocation } from 'react-router-dom';
import { PostActionPopupDto, StringWithLocaleDto } from '../../core/api/dtos';
import { injectTSDI } from '../../core/tsdi';
import { I18nStore } from '../../core/i18n-store';
import { LoaderFullPage } from '../loader';
import {
    openOverlay,
    OverlayComponentProps,
    OverlayNote,
    OverlayNoteButton
} from '../overlay';
import { ShareOnWhatsApp } from '../share-on-whatsapp';
import { findDisplayValue } from '../../core/multi-language';

export interface PostActionPopupConfig {
    request: WrapRequest<unknown>;
    postAction?: 'SHOW_POPUP' | 'REDIRECT';
    postActionPopupDto?: PostActionPopupDto;
    postActionUrls?: StringWithLocaleDto[];
    studioName: string;
    onCloseAfterSuccess(): void;
    showApiErrorMessage?: boolean;
}

const PostActionPopup = observer(
    (props: PostActionPopupConfig & OverlayComponentProps) => {
        const location = useLocation();
        const { translate } = injectTSDI(I18nStore);
        const {
            request,
            studioName,
            postAction,
            postActionUrls,
            postActionPopupDto = {},
            showApiErrorMessage
        } = props;

        const onClose = () => {
            if (request.fetched) {
                props.onCloseAfterSuccess();
            }
            props.closeOverlay();
        };

        const postActionUrlWithParams = `${findDisplayValue(postActionUrls)}${
            location.search
        }`;

        return (
            <section className="overlay" data-role="popup">
                <div className="overlay__bg" />
                {request.match({
                    loading: () => <LoaderFullPage />,
                    error: () => (
                        <OverlayNote position="center">
                            {showApiErrorMessage && request.error?.message ? (
                                request.error?.message
                            ) : (
                                <p>{translate('onlinepages.error')}</p>
                            )}
                            <OverlayNoteButton onClick={onClose}>
                                {translate('onlinepages.ok.all.clear')}
                            </OverlayNoteButton>
                        </OverlayNote>
                    ),
                    fetched: (result) => {
                        if (postAction === 'REDIRECT') {
                            window.open(postActionUrlWithParams, '_self');
                            return null;
                        }

                        if (!result) {
                            return null;
                        }

                        const {
                            headlines,
                            ctaTexts,
                            descriptions,
                            showShareOnWhatsApp
                        } = postActionPopupDto;

                        return (
                            <OverlayNote position="center">
                                <b>{findDisplayValue(headlines)}</b>
                                <p>{findDisplayValue(descriptions)}</p>
                                <OverlayNoteButton onClick={onClose}>
                                    {findDisplayValue(ctaTexts)}
                                </OverlayNoteButton>
                                {showShareOnWhatsApp && (
                                    <ShareOnWhatsApp
                                        url={window.location.href}
                                        studioName={studioName}
                                    />
                                )}
                            </OverlayNote>
                        );
                    }
                })}
            </section>
        );
    }
);

export const openPostActionPopup = (config: PostActionPopupConfig) => {
    openOverlay({
        id: 'post-action-popup',
        component: (props) => <PostActionPopup {...props} {...config} />
    });
};
